import React from 'react';
import { Box } from '@atlaskit/primitives/compiled';
import { useSidebar } from '@atlassian/jira-business-app-wrapper/src/controllers/sidebar/index.tsx';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { SingleSelectChartType } from '@atlassian/jira-issue-field-statistics/src/ui/single-select-chart-container/constants.tsx';
import { SidebarError } from '@atlassian/jira-issue-field-statistics/src/common/sidebar-error/index.tsx';
import { NumericStatsContainer } from '@atlassian/jira-issue-field-statistics/src/ui/numeric-stats-container-legacy/index.tsx';
import { NumericChart } from '@atlassian/jira-issue-field-statistics/src/ui/numeric-stats-container-legacy/constants.tsx';
import { SingleSelectChartContainer } from '@atlassian/jira-issue-field-statistics/src/ui/single-select-chart-container/index.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { getSidebarWidthFromStore } from '@atlassian/jira-business-app-wrapper/src/controllers/sidebar/utils.tsx';
import type { VisualRepresentation } from '../../../common/types.tsx';
import { getVisualRepresentation } from '../../../common/utils.tsx';
import { SIDEBAR_PADDING, SINGLE_SELECT_CHART_PADDING } from './constants.tsx';
import messages from './messages.tsx';

const shouldShowSingleSelectChart = (visualRepresentation: VisualRepresentation) =>
	visualRepresentation === 'SINGLE_SELECT_CHART';

const shouldShowStatisticsTable = (visualRepresentation: VisualRepresentation) =>
	visualRepresentation === 'STATISTICS_TABLE';

const calculateSingleSelectChartWidth = (sidebarWidth: number) =>
	sidebarWidth - SIDEBAR_PADDING - SINGLE_SELECT_CHART_PADDING;

type StatisticsChartProps = {
	selectedFieldId: string | undefined;
	selectedField: BusinessIssueField | undefined;
	selectedChartType: string | undefined;
	onChartTypeChange: (chartType: SingleSelectChartType) => void;
	jql: string;
};

export const StatisticsRenderer = ({
	selectedFieldId,
	selectedField,
	selectedChartType,
	onChartTypeChange,
	jql,
}: StatisticsChartProps) => {
	const { formatMessage } = useIntl();
	const [store] = useSidebar();
	const sidebarWidth = getSidebarWidthFromStore(store);
	const visualRepresentation = getVisualRepresentation(selectedField);

	if (selectedField) {
		return (
			<ContextualAnalyticsData
				attributes={{
					fieldId: selectedField.id,
				}}
			>
				{shouldShowSingleSelectChart(visualRepresentation) && (
					<SingleSelectChartContainer
						customFieldId={selectedField.id}
						query={jql}
						width={calculateSingleSelectChartWidth(sidebarWidth)}
						chartType={selectedChartType || 'DONUT_CHART'}
						onChartTypeChange={onChartTypeChange}
					/>
				)}
				{shouldShowStatisticsTable(visualRepresentation) && (
					<Box paddingBlock="space.300">
						<NumericStatsContainer
							customFieldId={selectedField.id}
							query={jql}
							chartType={NumericChart.TABLE}
						/>
					</Box>
				)}
			</ContextualAnalyticsData>
		);
	}

	// there is a field id present, but it could not be found in the selectable fields
	if (selectedFieldId !== undefined && selectedField === undefined) {
		return (
			<Box paddingBlock="space.300">
				<SidebarError errorMessage={formatMessage(messages.noFieldSelectedError)} />
			</Box>
		);
	}

	return null;
};
